import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, Database, RefreshCw, Trash2, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { QuestionGenerationPanel } from './QuestionGenerationPanel';
import { OperationRecovery } from './OperationRecovery';
import { PhaseImplementationSummary } from './PhaseImplementationSummary';

export const AdvancedQuestionTools: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);

  const scanForDuplicates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('interview_questions')
        .select('question_text');

      if (error) throw error;

      const textSet = new Set<string>();
      const duplicates: string[] = [];

      data?.forEach((q) => {
        const normalized = q.question_text.trim().toLowerCase();
        if (textSet.has(normalized)) {
          duplicates.push(q.question_text);
        } else {
          textSet.add(normalized);
        }
      });

      setDuplicateCount(duplicates.length);
      toast({
        title: 'Duplicate Scan Complete',
        description: `Found ${duplicates.length} duplicate questions`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const removeDuplicates = async () => {
    setLoading(true);
    try {
      // Get all questions
      const { data, error } = await supabase
        .from('interview_questions')
        .select('id, question_text')
        .order('created_at', { ascending: true });

      if (error) throw error;

      const seen = new Set<string>();
      const toDelete: string[] = [];

      data?.forEach((q) => {
        const normalized = q.question_text.trim().toLowerCase();
        if (seen.has(normalized)) {
          toDelete.push(q.id);
        } else {
          seen.add(normalized);
        }
      });

      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('interview_questions')
          .delete()
          .in('id', toDelete);

        if (deleteError) throw deleteError;

        toast({
          title: 'Duplicates Removed',
          description: `Deleted ${toDelete.length} duplicate questions`,
        });
        
        setDuplicateCount(0);
      } else {
        toast({
          title: 'No Duplicates',
          description: 'No duplicate questions found',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Implementation Summary */}
      <PhaseImplementationSummary />
      
      <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-900 dark:text-amber-100">
          Advanced Manual Tools
        </AlertTitle>
        <AlertDescription className="text-amber-800 dark:text-amber-200">
          These tools are for manual intervention only. The unified 100k system handles 
          automatic generation. Use these tools only when you need to:
          <ul className="list-disc ml-5 mt-2">
            <li>Manually generate questions for a specific role (testing/emergency)</li>
            <li>Scan and remove duplicate questions</li>
            <li>Resume failed operations</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Manual Role Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Manual Role-Specific Generator
          </CardTitle>
          <CardDescription>
            Emergency tool for generating questions for specific roles. 
            All automatic generation should use the unified 100k system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QuestionGenerationPanel />
        </CardContent>
      </Card>

      {/* Operation Recovery */}
      <OperationRecovery />

      {/* Duplicate Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Duplicate Question Management
          </CardTitle>
          <CardDescription>
            Scan for and remove duplicate questions from the database
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button onClick={scanForDuplicates} disabled={loading} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Scan for Duplicates
            </Button>
            
            {duplicateCount !== null && (
              <Badge variant={duplicateCount > 0 ? 'destructive' : 'default'}>
                {duplicateCount} duplicates found
              </Badge>
            )}
          </div>

          {duplicateCount !== null && duplicateCount > 0 && (
            <Alert className="border-red-500 bg-red-50 dark:bg-red-950">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800 dark:text-red-200">
                Found {duplicateCount} duplicate questions. These will be removed, keeping the oldest version.
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={removeDuplicates}
            disabled={loading || duplicateCount === null || duplicateCount === 0}
            variant="destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove All Duplicates
          </Button>
        </CardContent>
      </Card>

    </div>
  );
};
